import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Globe, Youtube, Facebook, Instagram, MessageCircle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

/**
 * "S&T auf einen Blick" Social Media bar.
 * Reads social links from /api/settings/social-links and renders
 * all configured buttons. Buttons with no URL are hidden.
 * Used in both Admin and Participant areas for a consistent shortcut bar.
 */
export const SocialMediaBar = ({ compact = false }) => {
  const [socialLinks, setSocialLinks] = useState({});

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/social-links`)
      .then(res => { if (!cancelled) setSocialLinks(res.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const buttons = [
    { key: "website", url: socialLinks.website, label: "Webseite", icon: Globe, color: "text-cyan-500", bg: "bg-cyan-500/10 hover:bg-cyan-500/20" },
    { key: "youtube", url: socialLinks.youtube, label: "YouTube", icon: Youtube, color: "text-red-500", bg: "bg-red-500/10 hover:bg-red-500/20" },
    { key: "tiktok", url: socialLinks.tiktok, label: "TikTok", icon: TikTokIcon, color: "text-foreground", bg: "bg-foreground/10 hover:bg-foreground/20" },
    { key: "facebook", url: socialLinks.facebook, label: "Facebook", icon: Facebook, color: "text-blue-600", bg: "bg-blue-600/10 hover:bg-blue-600/20" },
    { key: "instagram", url: socialLinks.instagram, label: "Instagram", icon: Instagram, color: "text-pink-500", bg: "bg-pink-500/10 hover:bg-pink-500/20" },
    { key: "whatsapp", url: socialLinks.whatsapp, label: "WhatsApp", icon: MessageCircle, color: "text-green-500", bg: "bg-green-500/10 hover:bg-green-500/20" },
    { key: "amazon_shop", url: socialLinks.amazon_shop, label: "Amazon Shop", icon: ExternalLink, color: "text-orange-500", bg: "bg-orange-500/10 hover:bg-orange-500/20" },
    { key: "google_play", url: socialLinks.google_play, label: "Google Play", icon: ExternalLink, color: "text-emerald-600", bg: "bg-emerald-600/10 hover:bg-emerald-600/20" },
  ];

  const visible = buttons.filter(b => b.url && b.url.trim());
  if (visible.length === 0) return null;

  return (
    <Card className="bg-card border-border" data-testid="social-media-bar">
      <CardHeader className={compact ? "py-3" : "pb-3"}>
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          S&T auf einen Blick
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? "pt-0 pb-3" : ""}>
        <div className="flex flex-wrap gap-2">
          {visible.map(b => {
            const Icon = b.icon;
            return (
              <a
                key={b.key}
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors font-medium text-sm ${b.bg} ${b.color}`}
                title={b.label}
                data-testid={`social-${b.key}`}
              >
                <Icon className="h-4 w-4" />
                {b.label}
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SocialMediaBar;
